// THE SESSION — the transport, the seams, and the handful of things that are genuinely the client's.
//
// WHAT LIVES HERE IS DELIBERATELY SMALL, and the test of whether something belongs is simple: could the
// server disagree with it? If it could, it is not the client's. So this holds the appeal's IDENTIFIER
// and what the owner has TYPED but not yet sent — and nothing else. Eligibility, entitlement, the
// value, the verdict, the caps and where the owner is in the flow all come back on every response.
//
// THE IDENTIFIER IS THE SERVER'S, ADOPTED (WEB-APPEAL-ID). A tab starts holding none; the first address resolve sends
// none and adopts the id the server minted; every later call names it; a reload in the same tab reads it back; the
// server refusing it as not on this account drops it. `./appealId.ts` says why each of those is so.
//
// The prototype's two mutable globals (`state` and `db`, app.js:109-117) held all of that plus the
// answers; that is the shape this replaces.

/* eslint-disable react-refresh/only-export-components --
   The provider and the hook that reads it belong together: splitting them puts the context object in a
   third module that neither side should import directly. The cost is a dev-time fast-refresh reload of
   this one file. */

import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Navigate } from "react-router-dom";
import type { PropertyTypeCode, ResolvePropertyAddressInput, ResolvePropertyResponse, V2Result } from "@contract";
import type { Transport } from "../transport/types";
import type { AppCheckTokens, AuthSession, Checkout } from "../services";
import type { AddressSuggester } from "../services/addressSuggest";
import type { SatelliteView } from "../services/satellite";
import { refusesAppeal, sessionAppealIdStore, watchAppealRefusals, type AppealIdStore } from "./appealId";
import { unitOrNull } from "./handIn";

/** An address resolve as a screen asks for one: everything but the id, which is the session's to send or omit. */
export type AddressResolve = Omit<ResolvePropertyAddressInput, "appealId">;

export interface Session {
  readonly transport: Transport;
  readonly checkout: Checkout;
  readonly auth: AuthSession;
  readonly appCheck: AppCheckTokens;
  /** Which appeal this tab is looking at: the id the server minted and this tab adopted, or null before the first
   *  resolve (and after a reload with no storage, or a stale id dropped). An identifier, not a decision. A screen that
   *  needs one renders under `RequireAppeal` and reads it with `useAppealId`. */
  readonly appealId: string | null;
  /** THE one way to resolve an address: names the held appeal or, holding none, sends no id and adopts the one the
   *  server answers. A held id the server says is not on this account is dropped, and the address starts afresh.
   *  It also carries the owner's property-type pick: see `propertyType`. */
  resolveAddress(input: AddressResolve): Promise<V2Result<ResolvePropertyResponse>>;
  /** THE OWNER'S PROPERTY-TYPE PICK for the address and unit it was made on, or null (WEB-PARITY-1B, PR-5). A resolve
   *  that names a type sets it; every later resolve of the same address and unit that names none SENDS it, because the
   *  server stores an absent type as null (`resolve/address.ts`) and `SubjectView.propertyType` carries only the
   *  owner's pick — so `/risk`'s re-ask used to wipe the pick `/property` had just sent, and `/details` said "not in
   *  the record". Another address or unit drops it; so does letting go of the appeal. */
  readonly propertyType: PropertyTypeCode | null;
  /** Drops the held id — the account it belonged to was just deleted. */
  forgetAppeal(): void;
  /** WEB-PARITY-7 (HM-5): holds one of THIS ACCOUNT'S appeals, picked from the server's own list on `/home` — the app's
   *  property page reopening an appeal (HomeDetailView.swift:79-80). The id is the server's, never typed; one the server
   *  says is not on this account is dropped by the same watch every call has. */
  openAppeal(appealId: string): void;
  /** What the owner typed into the address box, before the server has resolved it. */
  readonly typedAddress: string;
  setTypedAddress(value: string): void;
  /** What the owner typed into the unit box (WEB-PAY-2), sent beside the address on every resolve; "" is no unit. */
  readonly typedUnit: string;
  setTypedUnit(value: string): void;
  /** Whether this tab runs on `FakeTransport`. Only decides whether the fake's sample addresses are offered: a build
   *  talking to a real server must not say it talks to a fake one. */
  readonly onFake: boolean;
  /** Leaves this app for another page — the free check's hand-off (WEB-PAY-2). A seam, so a test can see where. */
  leave(url: string): void;
  /** Apple's address suggestions for the box on `/` (WEB-SUGGEST), or null for a plain box. A seam, so a test can fake
   *  Apple and the token function. One per page load, so its caps are per page. */
  readonly addressSuggester: AddressSuggester | null;
  /** Apple's satellite view of the property on step 1 (WEB-PARITY-1C), or null for none. A seam, as the suggester is. */
  readonly satelliteView: SatelliteView | null;
  /** THE "NEWS AND OFFERS" TICK (A9) FROM CREATING AN ACCOUNT (ACC-3a). Since ACC-3b the server RECORDS it — `v2Credits`
   *  EM-1's `contactSet`, against the hash of the exact words ticked (F-ACC3a-1, closed) — and the account's settings
   *  read the standing answer back from the server; this copy is only what this page load sent. Null until an account is
   *  created on this page load. */
  readonly marketingTick: MarketingTick | null;
  setMarketingTick(tick: MarketingTick | null): void;
  /** Bumping this re-runs every `useCall` under it: the client's "ask the server again". */
  readonly revision: number;
  refresh(): void;
}

/** One A9 answer, as the house consent pattern would record it: the answer, the held sentence's id and the SHA-256 of
 *  its words (null where this browser has no Web Crypto), and when. */
export interface MarketingTick {
  readonly ticked: boolean;
  readonly copyId: "account.create.marketing";
  readonly textSha256: string | null;
  readonly atMs: number;
}

const SessionContext = createContext<Session | null>(null);

/** The owner's type pick, and the address and unit it was made for. */
interface TypePick {
  readonly typedAddress: string;
  readonly unit: string | null;
  readonly propertyType: PropertyTypeCode;
}

/** A resolve with the held pick added when it names no type of its own and asks about the address the pick was made
 *  for. Returns the input itself when nothing is added. */
function withTypePick(input: AddressResolve, held: TypePick | null): AddressResolve {
  if (input.propertyType !== undefined && input.propertyType !== null) return input;
  if (held === null || held.typedAddress !== input.typedAddress || held.unit !== (input.unit ?? null)) return input;
  return { ...input, propertyType: held.propertyType };
}

export interface SessionProviderProps {
  transport: Transport;
  checkout: Checkout;
  auth: AuthSession;
  appCheck: AppCheckTokens;
  /** Where this tab keeps the appeal id across a reload. Defaults to the tab's `sessionStorage`. */
  appealIds?: AppealIdStore;
  initialTypedAddress?: string;
  initialTypedUnit?: string;
  /** Defaults to false: nothing claims to be the fake unless whoever built the transport says so. */
  onFake?: boolean;
  /** Defaults to a full-page navigation in this tab. */
  leave?: (url: string) => void;
  /** Defaults to none: a plain address box. `main.tsx` passes the browser's (`createBrowserSuggester`). */
  addressSuggester?: AddressSuggester | null;
  /** Defaults to none: no image. `main.tsx` passes the browser's (`createBrowserSatellite`). */
  satelliteView?: SatelliteView | null;
  children: ReactNode;
}

/** A full-page navigation, the way `createBrowserCheckout` leaves for Stripe: `window.open(url, "_self")` rather than
 *  anything on `window.location`, which this package never touches (stagingSeams.test.ts, "NO FALLBACK ORIGIN"). */
function leaveByNavigating(url: string): void {
  window.open(url, "_self", "noopener");
}

export function SessionProvider(props: SessionProviderProps) {
  const [typedAddress, setTypedAddress] = useState(props.initialTypedAddress ?? "");
  const [typedUnit, setTypedUnit] = useState(props.initialTypedUnit ?? "");
  const onFake = props.onFake ?? false;
  const leave = props.leave ?? leaveByNavigating;
  const addressSuggester = props.addressSuggester ?? null;
  const satelliteView = props.satelliteView ?? null;
  const [revision, setRevision] = useState(0);
  const [marketingTick, setMarketingTick] = useState<MarketingTick | null>(null);

  // THE HELD ID: state for rendering, and a ref that is current the instant it changes, because an answer can land
  // between renders and must not act on an id the tab has already dropped or replaced.
  const [defaultStore] = useState(() => sessionAppealIdStore());
  const store = props.appealIds ?? defaultStore;
  const [appealId, setAppealId] = useState<string | null>(() => store.read());
  const held = useRef(appealId);
  const adopt = useCallback((id: string) => {
    held.current = id;
    setAppealId(id);
    store.write(id);
  }, [store]);
  // THE PICK: state for rendering, a ref for the resolve that reads it between renders — the same pattern as the id.
  const [typePick, setTypePick] = useState<TypePick | null>(null);
  const pick = useRef<TypePick | null>(null);
  const setPick = useCallback((next: TypePick | null) => {
    pick.current = next;
    setTypePick(next);
  }, []);
  const forgetAppeal = useCallback(() => {
    held.current = null;
    setAppealId(null);
    store.clear();
    setPick(null);
  }, [store, setPick]);
  const openAppeal = useCallback((id: string) => {
    if (held.current === id) return;
    setPick(null);
    adopt(id);
  }, [adopt, setPick]);
  const transport = useMemo(
    () => watchAppealRefusals(props.transport, (refused) => {
      if (held.current === refused) forgetAppeal();
    }),
    [props.transport, forgetAppeal],
  );

  // ONE MINT AT A TIME. Two resolves sent with no id would make two appeals, and the second would orphan the first; so
  // while one is out, the next waits for it and then names what it minted.
  const minting = useRef<Promise<void> | null>(null);
  const resolveAddress = useCallback(
    async function resolve(asked: AddressResolve): Promise<V2Result<ResolvePropertyResponse>> {
      const input = withTypePick(asked, pick.current);
      if (asked.propertyType !== undefined && asked.propertyType !== null) {
        setPick({ typedAddress: asked.typedAddress, unit: asked.unit ?? null, propertyType: asked.propertyType });
      } else if (input === asked && pick.current !== null) {
        setPick(null);
      }
      while (held.current === null && minting.current !== null) await minting.current;
      const sent = held.current;
      let release = () => {};
      if (sent === null) minting.current = new Promise<void>((done) => { release = done; });
      try {
        const result = await transport.v2ResolveProperty.address(sent === null ? input : { ...input, appealId: sent });
        if (result.ok) {
          const answered = result.response.appeal?.appealId;
          if (answered !== undefined && answered !== held.current) adopt(answered);
          return result;
        }
        if (sent !== null && refusesAppeal(result)) {
          // THE HELD ID WAS STALE. The server changed nothing; this address starts a new appeal, sent with no id.
          if (held.current === sent) forgetAppeal();
          return resolve(input);
        }
        return result;
      } finally {
        if (sent === null) {
          minting.current = null;
          release();
        }
      }
    },
    [transport, adopt, forgetAppeal, setPick],
  );
  const propertyType =
    typePick !== null && typePick.typedAddress === typedAddress && typePick.unit === unitOrNull(typedUnit)
      ? typePick.propertyType
      : null;

  const value = useMemo<Session>(
    () => ({
      transport,
      checkout: props.checkout,
      auth: props.auth,
      appCheck: props.appCheck,
      appealId,
      resolveAddress,
      propertyType,
      forgetAppeal,
      openAppeal,
      typedAddress,
      setTypedAddress,
      typedUnit,
      setTypedUnit,
      onFake,
      leave,
      addressSuggester,
      satelliteView,
      marketingTick,
      setMarketingTick,
      revision,
      refresh: () => setRevision((r) => r + 1),
    }),
    [transport, props.checkout, props.auth, props.appCheck, appealId, resolveAddress, propertyType, forgetAppeal, openAppeal, typedAddress, typedUnit, onFake, leave, addressSuggester, satelliteView, marketingTick, revision],
  );
  return <SessionContext.Provider value={value}>{props.children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (session === null) throw new Error("useSession outside a SessionProvider");
  return session;
}

const HeldAppealContext = createContext<string | null>(null);

/**
 * A screen that needs an appeal renders under this. Holding none — a fresh tab, a reload whose storage failed, a stale
 * id just dropped — the owner is sent to `/` to start, with no sentence of this client's own; or, for an owner signed in
 * to an account, to `otherwise`, which the shell sets to `/home`, their properties (WALK-FIX-1: a step opened with no
 * appeal held bounced Tyler to a blank new appeal, when his finished one was a tap away on `/home`).
 */
export function RequireAppeal({ children, otherwise = "/", pending = null }: {
  children: ReactNode;
  /** Null while the shell cannot yet say where (the server's account switch unanswered): `pending` shows meanwhile. */
  otherwise?: string | null;
  pending?: ReactNode;
}) {
  const { appealId } = useSession();
  if (appealId === null) return otherwise === null ? <>{pending}</> : <Navigate to={otherwise} replace />;
  return <HeldAppealContext.Provider value={appealId}>{children}</HeldAppealContext.Provider>;
}

/** The held appeal's id, for a screen under `RequireAppeal` — which is the only place one can be read. */
export function useAppealId(): string {
  const appealId = useContext(HeldAppealContext);
  if (appealId === null) throw new Error("useAppealId outside a RequireAppeal");
  return appealId;
}
